import { useTranslation } from 'react-i18next';
import { IAssociation, IQuestion } from '@quizzach/interfaces';
import {
	AssociationContainer,
	QuestionText,
	QuestionContainer,
} from '@quizzach/play-styles';
import { AnswerReveal, PointsRow } from '@quizzach/components';

interface IAssociationQuestionProps {
	color: string;
	currentQuestionNumber: number;
	currentQuestion: IQuestion;
	totalQuestions?: number;
}

interface IAssociationQuestionAnswerProps {
	questionContainerColor: string;
	answerContainerColor: string;
	currentQuestionNumber: number;
	currentQuestion: IQuestion;
	totalQuestions?: number;
}

const columns = ['a', 'b', 'c', 'd'] as const;
const rows = ['1', '2', '3', '4'] as const;

/* Proposal grid: the 16 clues are always revealed (the presenter reads
   them) and stack row-major so each column's clues sit above its bottom
   cell — the muted column letter on the question slide, the brand-colored
   column solution on the answer slide. */
const AssociationGrid = ({
	currentQuestion,
	open,
}: {
	currentQuestion: IQuestion;
	open?: boolean;
}) => {
	const { t } = useTranslation();
	const aq = currentQuestion.association_question;
	if (!aq) return null;

	return (
		<AssociationContainer>
			{rows.map((row) =>
				columns.map((column) => (
					<div
						key={`${column}${row}`}
						className='cell clue'
						dangerouslySetInnerHTML={{
							__html: aq[`${column}${row}` as keyof IAssociation] || '',
						}}
					/>
				))
			)}
			{columns.map((column) =>
				open ? (
					<div
						key={column}
						className='cell sol'
						dangerouslySetInnerHTML={{ __html: aq[column] || '' }}
					/>
				) : (
					<div key={column} className='cell letter'>
						{column.toUpperCase()}
					</div>
				)
			)}
			{!open && (
				<div className='cell letter final-row'>
					{t('common.finalLine1')} {t('common.finalLine2')}
				</div>
			)}
		</AssociationContainer>
	);
};

export const AssociationQuestionDesktop = (
	props: IAssociationQuestionProps
) => {
	const { t } = useTranslation();
	const { color, currentQuestion } = props;

	return (
		<>
			<QuestionContainer color={color}>
				<QuestionText>{t('quiz.associationInstruction')}</QuestionText>
			</QuestionContainer>
			<AssociationGrid currentQuestion={currentQuestion} />
			<PointsRow
				points={currentQuestion.points}
				explanation={currentQuestion.points_explanation}
			/>
		</>
	);
};

export const AssociationQuestionAnswerDesktop = (
	props: IAssociationQuestionAnswerProps
) => {
	const { currentQuestion } = props;

	return (
		<>
			<AssociationGrid currentQuestion={currentQuestion} open />
			<AnswerReveal
				revealKey={currentQuestion.uuid}
				answerHtml={currentQuestion.association_question?.answer || ''}
				explanation={currentQuestion.association_question?.explanation}
			/>
			<PointsRow
				points={currentQuestion.points}
				explanation={currentQuestion.points_explanation}
			/>
		</>
	);
};
